import React, { useEffect, useMemo, useRef } from "react";
import { graphql, useStaticQuery } from "gatsby";
import styled from "styled-components";
import sr from "@utils/sr";
import { srConfig } from "@config";
import { usePrefersReducedMotion } from "@hooks";

const StyledPublicationsPage = styled.main`
  width: 100%;
`;

const HeroSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 170px clamp(28px, 6vw, 80px) 90px;

  .eyebrow {
    margin: 0 0 18px;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: 13px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  h1 {
    margin: 0 0 22px;
    color: var(--text-primary);
    font-size: clamp(42px, 6vw, 82px);
    line-height: 0.98;
    letter-spacing: -0.05em;
    max-width: 820px;
  }

  .lead {
    margin: 0;
    max-width: 760px;
    color: var(--text-secondary);
    font-size: clamp(18px, 2vw, 23px);
    line-height: 1.8;
  }

  .lead strong {
    color: var(--text-primary);
    font-weight: 600;
  }
`;

const IntroSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 32px;

  p {
    margin: 0;
    max-width: 760px;
    color: var(--text-secondary);
    font-size: 17px;
    line-height: 1.9;
  }
`;

const PublicationsSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 150px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 18px;

  @media (max-width: 980px) {
    gap: 16px;
  }
`;

const Card = styled.article`
  grid-column: span 6;
  padding: 26px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
  transition: var(--transition);

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 14px 34px rgba(0, 0, 0, 0.06),
      inset 0 1px 0 rgba(255, 255, 255, 0.42);
  }

  @media (max-width: 980px) {
    grid-column: span 12;
  }
`;

const Header = styled.div`
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0;
  color: var(--text-primary);
  font-size: clamp(26px, 3vw, 34px);
  line-height: 1.12;
  letter-spacing: -0.03em;
  font-weight: 600;

  a {
    color: inherit;
    text-decoration: none;
  }

  a:hover,
  a:focus-visible {
    color: var(--text-primary);
    text-decoration: underline;
    text-underline-offset: 4px;
    outline: none;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 999px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.62);
  border: 1px solid var(--border-light);
`;

const Abstract = styled.div`
  margin-top: 14px;

  p {
    margin: 0;
    color: var(--text-secondary);
    line-height: 1.9;
    font-size: 15px;
  }

  p + p {
    margin-top: 12px;
  }

  strong {
    color: var(--text-primary);
    font-weight: 600;
  }
`;

const TagRow = styled.div`
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  padding: 7px 10px;
  border-radius: 999px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.56);
  border: 1px solid var(--border-light);
`;

const Actions = styled.div`
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 14px;
  border-radius: 12px;
  text-decoration: none;
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.64);
  border: 1px solid var(--border-light);
  transition: var(--transition);

  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.82);
    border-color: var(--border-medium);
    color: var(--text-primary);
  }

  &:focus-visible {
    outline: 2px solid rgba(17, 17, 17, 0.16);
    outline-offset: 2px;
  }
`;

const EmptyState = styled.div`
  padding: 20px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.42);
  border: 1px solid var(--border-light);
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.8;
`;

const formatDate = iso => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-AU", { month: "long", year: "numeric" });
};

const PublicationsGrid = () => {
  const data = useStaticQuery(graphql`
    query PublicationsPageQuery {
      pubs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/publications/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              date
              publisher
              venue
              tags
              external
              doi
              pdf
            }
            html
          }
        }
      }
    }
  `);

  const publications = useMemo(
    () => data?.pubs?.edges?.map(edge => edge.node) ?? [],
    [data]
  );

  if (!publications.length) {
    return (
      <EmptyState>
        No publications found yet.
      </EmptyState>
    );
  }

  return (
    <Grid>
      {publications.map((publication, i) => {
        const { frontmatter, html } = publication;
        const { title, date, publisher, venue, tags, external, doi, pdf } = frontmatter;

        const primaryUrl = external || pdf || (doi ? `https://doi.org/${doi}` : null);

        return (
          <Card key={`${title}-${i}`}>
            <Header>
              <Title>
                {primaryUrl ? (
                  <a href={primaryUrl} target="_blank" rel="noopener noreferrer">
                    {title}
                  </a>
                ) : (
                  title
                )}
              </Title>

              <Meta>
                {date ? <Pill>{formatDate(date)}</Pill> : null}
                {publisher ? <Pill>{publisher}</Pill> : null}
                {venue ? <Pill>{venue}</Pill> : null}
              </Meta>
            </Header>

            <Abstract dangerouslySetInnerHTML={{ __html: html }} />

            {Array.isArray(tags) && tags.length > 0 ? (
              <TagRow aria-label="Publication tags">
                {tags.map((tag, idx) => (
                  <Tag key={`${tag}-${idx}`}>{tag}</Tag>
                ))}
              </TagRow>
            ) : null}

            <Actions aria-label="Publication links">
              {pdf ? (
                <ActionLink href={pdf} target="_blank" rel="noopener noreferrer">
                  PDF
                </ActionLink>
              ) : null}

              {doi ? (
                <ActionLink href={`https://doi.org/${doi}`} target="_blank" rel="noopener noreferrer">
                  DOI
                </ActionLink>
              ) : null}

              {external ? (
                <ActionLink href={external} target="_blank" rel="noopener noreferrer">
                  Publisher
                </ActionLink>
              ) : null}
            </Actions>
          </Card>
        );
      })}
    </Grid>
  );
};

const PublicationsPageContent = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion || !revealContainer.current) return;
    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  return (
    <StyledPublicationsPage ref={revealContainer}>
      <HeroSection>
        <p className="eyebrow">Publications</p>
        <h1>Published work, research, and formal writing.</h1>
        <p className="lead">
          A selection of written work spanning <strong>technology, systems thinking, cybernetics, and emerging digital ideas</strong>.
        </p>
      </HeroSection>

      <IntroSection>
        <p>
          These publications reflect an earlier part of my technical journey: thinking through
          emerging technologies, human-computer interaction, and broader questions around systems,
          intelligence, and digital transformation.
        </p>
      </IntroSection>

      <PublicationsSection>
        <PublicationsGrid />
      </PublicationsSection>
    </StyledPublicationsPage>
  );
};

export default PublicationsPageContent;